// Refresh-token rotation, ported from the add-in's refreshAuth.ts: single-flight (concurrent 401s share one
// refresh), and `refresh_token_reused` from the API means the session was stolen — every token is cleared.
import { readToken, writeToken, clearTokens } from './tokenStore';

export const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api';
const REFRESH_PATH = '/token/refresh';
const REUSE_DETAIL = 'refresh_token_reused';

let inFlight: Promise<boolean> | null = null;
let accessTokenAtRefreshStart: string | null = null;

export function getAccessToken(): string | null {
  return readToken('accessToken');
}

export function getRefreshToken(): string | null {
  return readToken('refreshToken');
}

export function storeTokens(args: { access_token: string; token_type: string; refresh_token?: string | null }): void {
  writeToken('accessToken', args.access_token);
  writeToken('tokenType', args.token_type || 'bearer');
  if (args.refresh_token) writeToken('refreshToken', args.refresh_token);
}

export function clearAuth(): void {
  clearTokens();
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  // Another tab may have rotated since we last looked: adopt its access token instead of racing it.
  const currentAccess = getAccessToken();
  if (accessTokenAtRefreshStart !== null && currentAccess !== null && currentAccess !== accessTokenAtRefreshStart) return true;

  const response = await fetch(`${BASE_URL}${REFRESH_PATH}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).catch(() => null);
  if (!response) return false;
  if (!response.ok) {
    if (response.status === 401) {
      const detail = await response
        .json()
        .then((b: { detail?: unknown }) => (typeof b.detail === 'string' ? b.detail : ''))
        .catch(() => '');
      if (detail === REUSE_DETAIL) clearTokens();
    }
    return false;
  }
  try {
    const data = (await response.json()) as { access_token: string; token_type: string; refresh_token: string };
    storeTokens(data);
    return true;
  } catch {
    return false;
  }
}

export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight;
  accessTokenAtRefreshStart = getAccessToken();
  inFlight = doRefresh().finally(() => {
    inFlight = null;
    accessTokenAtRefreshStart = null;
  });
  return inFlight;
}

// The refresh token alone is enough for the API to revoke the session, so a sign-out after the access token expired
// still closes it on the server; the bearer goes along when there is one.
export async function logout(): Promise<void> {
  const accessToken = readToken('accessToken');
  const tokenType = readToken('tokenType');
  const refreshToken = getRefreshToken();
  const bearer: Record<string, string> = accessToken && tokenType ? { Authorization: `${tokenType} ${accessToken}` } : {};
  if (refreshToken || bearer.Authorization) {
    await fetch(`${BASE_URL}/logout`, {
      method: 'POST',
      headers: { accept: 'application/json', 'Content-Type': 'application/json', ...bearer },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => null);
  }
  clearTokens();
}
